import { useState, useRef, useCallback, useEffect } from "react";
import { useEthosNotification } from "../../contexts/ethos-notification-context";
import { useServiceCustomer } from "../../contexts/service-customer-context";
import {
  getProgressStatusLabelFromConfiguration,
  isExpired,
  renderPaginator,
} from "../../utils/functions";
import { debounce } from "lodash";
import {
  faSort,
  faSortUp,
  faSortDown,
  faEdit,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink } from "react-router-dom";
import moment from "moment";
import { useConfigurations } from "../../contexts/configurations-context";
import { useService } from "../../contexts/service-context";
import "./style.css";
import { DeleteReportType } from "../../models";

const ListDeleteReport = () => {
  const { configurations } = useConfigurations();
  const { getServiceCustomers } = useServiceCustomer();
  const { handleError } = useEthosNotification();
  const { getServices } = useService();
  const LIMIT = 12;
  const searchRef = useRef<HTMLInputElement>();
  const sortTypeFilterRef = useRef<HTMLSelectElement>();
  const sortStatusFilterRef = useRef<HTMLSelectElement>();

  const [pagination, setPagination] = useState(null);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState<string | null>(null);

  const [deleteReportRequest, setDeleteReportRequest] = useState([]);

  const [selectedReportType, setSelectedReportType] =
    useState<DeleteReportType | null>(null);
  const [selectReportStatus, setSelectReportStatus] = useState<string | null>(
    null,
  );

  const [services, setServices] = useState(null);

  const [sortColumn, setSortColumn] = useState({ column: null, direction: 0 });
  const [sortFilter, setSortFilter] = useState(null);

  const initDeleteReports = useCallback(async () => {
    const { data, error } = await getServiceCustomers(
      selectedReportType === null ? DeleteReportType.ALL : selectedReportType,
      LIMIT,
      offset,
      sortFilter,
      search,
      null,
      selectReportStatus,
    );
    if (error) {
      handleError(error);
      return;
    }
    setDeleteReportRequest(data.data);
    renderPaginator(data.length, LIMIT, setOffset, setPagination, offset);
  }, [
    getServiceCustomers,
    handleError,
    offset,
    search,
    selectReportStatus,
    selectedReportType,
    sortFilter,
  ]);

  const init = useCallback(() => {
    initDeleteReports();
  }, [initDeleteReports]);

  const resetFilter = () => {
    setOffset(0);
    sortTypeFilterRef.current.value = null;
    setSortFilter(null);
    setSearch(null);
    searchRef.current.value = null;
    setSelectedReportType(DeleteReportType.ALL);
    setSelectReportStatus(null);
    sortStatusFilterRef.current.value = null;
  };

  const getRowColorStatus = (status: string): string => {
    switch (status) {
      case "KO":
        return "red";
      case "COMPLETATO":
        return "green";
      default:
        return "";
    }
  };
  const onSearchChange = debounce((value: string) => {
    setSearch(value);
  }, 500);

  const onChangeType = debounce((value: DeleteReportType) => {
    setSelectedReportType(value);
  }, 500);

  const onChangeStatus = debounce((value: DeleteReportType) => {
    setSelectReportStatus(value);
  }, 500);

  const loadServices = useCallback(async () => {
    var { data, error } = await getServices();

    if (error) {
      handleError(error);
      return;
    }
    setServices(data.data);
  }, [getServices, handleError]);

  const sortingColumn = (columnName: string) => {
    if (!!sortColumn) {
      if (sortColumn.column === columnName) {
        setSortColumn({
          column: columnName,
          direction: sortColumn.direction === 1 ? -1 : 1,
        });
      } else {
        setSortColumn({ column: columnName, direction: 1 });
      }
    }
  };

  const renderSortArrow = (columnName: string) => {
    if (sortFilter === null) {
      return (
        <FontAwesomeIcon
          icon={faSort}
          style={{ opacity: ".50" }}
          className="ml-2"
        />
      );
    }
    if (!!sortColumn) {
      if (sortColumn.column === columnName) {
        return sortColumn.direction === 1 ? (
          <FontAwesomeIcon icon={faSortUp} className="ml-2" />
        ) : (
          <FontAwesomeIcon icon={faSortDown} className="ml-2" />
        );
      }
    }
  };

  const getCustomerFullname = (item): string => {
    const customer = item.customerData.find((c) => c._id === item.customerId);
    if (!!customer) {
      return `${customer.first_name} ${customer.last_name}`;
    }
    return "N.D.";
  };

  const isLoaded = () => {
    return services && configurations;
  };

  useEffect(() => {
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    init();
  }, [offset, init]);

  useEffect(() => {
    if (!!sortColumn.column) {
      setSortFilter(`${sortColumn.column}:${sortColumn.direction}`);
    }
  }, [sortColumn]);

  return (
    <>
      {isLoaded() && (
        <div className="row">
          {/* FILTER AREA */}
          <div className="col-12 text-center">
            <h3>Lista dei servizi di cancellazione</h3>
          </div>
          <div className="bg-light">
            <div className="row align-items-center bg-light ">
              <div className="col-12 col-md-12 col-lg-3 my-2">
                <input
                  ref={searchRef}
                  className="form-control"
                  placeholder="Cerca un cliente"
                  onChange={(e) => {
                    onSearchChange(e.target.value);
                  }}
                />
              </div>
              <div className="col-12 col-md-12 col-lg-3">
                <select
                  title="select type"
                  ref={sortTypeFilterRef}
                  className="form-select d-inline w-100 my-2"
                  onChange={(e) => {
                    if (e.target.value === "null") {
                      setSelectedReportType(null);
                    } else {
                      onChangeType(e.target.value as DeleteReportType);
                    }
                    setOffset(0);
                  }}
                >
                  <option value="null">Filtra per Tipologia</option>
                  <option value={DeleteReportType.DELETESIC}>
                    Cancellazione Segnalazioni SIC
                  </option>
                  <option value={DeleteReportType.DELETEPROP}>
                    Cancellazione Segnalazioni Protesti
                  </option>
                </select>
              </div>
              <div className="col-12 col-md-12 col-lg-3">
                <select
                  title="select status"
                  ref={sortStatusFilterRef}
                  className="form-select d-inline w-100 my-2"
                  onChange={(e) => {
                    if (e.target.value === "null") {
                      setSelectReportStatus(null);
                    } else {
                      onChangeStatus(e.target.value as DeleteReportType);
                    }
                    setOffset(0);
                  }}
                >
                  <option value="null">Filtra per Avanzamento</option>
                  {(
                    configurations.typeServiceStatus
                      ?.cancellazioneSegnalazioniProtesti ?? []
                  ).map((item, index) => {
                    return (
                      <option key={index} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col-12 col-md-12 col-lg-3 my-2">
                <div className="btn-group w-100">
                  <button className="btn btn-primary">Cerca</button>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      resetFilter();
                    }}
                  >
                    Resetta Filtri
                  </button>
                </div>
              </div>
            </div>
          </div>
          <hr className="my-3" />
          {/* PAGINATOR */}
          <div className="col-12 mt-1">
            {deleteReportRequest && deleteReportRequest.length > 0 ? (
              <nav className="pagination-section">
                <ul className="pagination justify-content-center">
                  {pagination}
                </ul>
              </nav>
            ) : null}
          </div>

          {/* TABLE */}
          <table className="table table-striped">
            <thead className="thead-dark">
              <tr>
                <th style={{ width: "5%" }}></th>
                <th></th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("customerData.first_name");
                  }}
                >
                  Cliente {renderSortArrow("customerData.first_name")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("bank");
                  }}
                >
                  Banca {renderSortArrow("bank")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("nContract");
                  }}
                >
                  N° Contratto {renderSortArrow("nContract")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("contractType");
                  }}
                >
                  Tipo Contratto {renderSortArrow("contractType")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("serviceStatus.status");
                  }}
                >
                  Stato Avanzamento {renderSortArrow("serviceStatus.status")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("createdAt");
                  }}
                >
                  Data Creazione {renderSortArrow("createdAt")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("serviceStatus.expireAt");
                  }}
                >
                  Data Scadenza {renderSortArrow("serviceStatus.expireAt")}
                </th>
              </tr>
            </thead>

            <tbody>
              {deleteReportRequest && deleteReportRequest.length > 0 ? (
                deleteReportRequest.map((del, index) => {
                  const canEdit =
                    del.status === "ACTIVE" && del.serviceStatus != null;

                  return (
                    <tr
                      key={index}
                      className={getRowColorStatus(
                        del.serviceStatus !== null
                          ? del.serviceStatus.status
                          : "N.D.",
                      )}
                    >
                      <td>
                        {canEdit ? (
                          <NavLink
                            to={`service-details/${del._id}`}
                            className="text-white"
                          >
                            <button title="edit" className="btn btn-secondary">
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          </NavLink>
                        ) : (
                          <button
                            title="edit"
                            className="btn btn-secondary"
                            disabled
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        )}
                      </td>
                      <td>
                        {del.serviceStatus === null ||
                        del.serviceStatus.expireAt === null ? (
                          ""
                        ) : moment(del.serviceStatus.expireAt) >
                          moment() ? null : (
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: "#f00" }}
                          />
                        )}
                      </td>
                      <td className="text-uppercase">
                        <a href={"/customers/" + del.customerId}>
                          <span style={{ color: "black" }}>
                            {getCustomerFullname(del)}
                          </span>
                        </a>
                      </td>
                      <td className="text-uppercase">{del?.bank || "N.D."}</td>
                      <td className="text-uppercase">
                        {del?.nContract || "N.D."}
                      </td>
                      <td className="text-uppercase">
                        {del?.contractType || "N.D."}
                      </td>

                      <td className="text-uppercase">
                        {del.serviceStatus !== null
                          ? getProgressStatusLabelFromConfiguration(
                              del.serviceStatus.status,
                              configurations,
                              "default",
                            )
                          : "N.D."}
                      </td>
                      <td>{moment(del.createdAt).format("DD/MM/YYYY")}</td>
                      <td
                        className={isExpired(
                          del.serviceStatus === null ||
                            del.serviceStatus.expireAt === null
                            ? "N.D."
                            : moment(del.serviceStatus.expireAt).format(
                                "DD/MM/YYYY",
                              ),
                        )}
                      >
                        {del.serviceStatus === null ||
                        del.serviceStatus.expireAt === null
                          ? "N.D."
                          : moment(del.serviceStatus.expireAt).format(
                              "DD/MM/YYYY",
                            )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <th colSpan={9} style={{ textAlign: "center" }}>
                    Non è stato trovato nessun servizio di cancellazione
                  </th>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default ListDeleteReport;
